import { useState, useRef, useEffect } from 'react';
import { Fab, Box, Typography, Paper, IconButton, CircularProgress, Modal, Link, Tooltip, Snackbar, Alert, Button } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import styles from './chat.module.css';
import * as roiChatService from '../../../services/roiChatService';
import owlLogo from '../../../assets/images/logos/ea-icon-small.png';

const termsStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    maxWidth: 600,
    minWidth: 300,
    maxHeight: '90vh',
    width: '90%',
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 4,
    overflowY: 'auto'
};

const TypingIndicator = () => (
    <div className={styles.typingIndicator}>
        <span></span>
        <span></span>
        <span></span>
    </div>
);

export function Chat() {
    const [openChat, setOpenChat] = useState(false);
    const [message, setMessage] = useState('');
    const [messages, setMessages] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [hasCheckedMessages, setHasCheckedMessages] = useState(false);
    const [showTermsModal, setShowTermsModal] = useState(false);
    const [showInfoModal, setShowInfoModal] = useState(false);
    const [showErrorSnackbar, setShowErrorSnackbar] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMoreMessages, setHasMoreMessages] = useState(true);
    const [enableScroll, setEnableScroll] = useState(false);
    const messagesEndRef = useRef(null);
    const chatContentRef = useRef(null);
    const autoOpenTimeoutRef = useRef(null);
    const lastScrollHeightRef = useRef(0);
    const lastScrollTopRef = useRef(0);

    const scrollToBottom = (behavior = "smooth") => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior });
        }
    };

    // Effect to handle initial scroll and enable scroll handler
    useEffect(() => {
        if (openChat && messages.length > 0 && !enableScroll) {
            // Force immediate scroll to bottom
            scrollToBottom("auto");
            
            // Enable scroll handler after a delay
            const timeoutId = setTimeout(() => {
                setEnableScroll(true);
            }, 500);
            
            return () => clearTimeout(timeoutId);
        }
    }, [messages, openChat]);

    // Effect to scroll to bottom only for new messages (not loaded history)
    useEffect(() => {
        if (openChat && messages.length > 0 && currentPage === 1 && !isLoadingMore) {
            scrollToBottom();
        }
    }, [messages.length]);

    useEffect(() => {
        const checkAndOpenChat = async () => {
            if (!openChat) {
                const token = localStorage.getItem('token');
                try {
                    const response = await roiChatService.getMessages(token);
                    if (response.status && response.data.length === 0) {
                        autoOpenTimeoutRef.current = setTimeout(() => {
                            setOpenChat(true);
                        }, 30000);
                    }
                } catch (err) {
                    console.error('Error checking messages:', err);
                    autoOpenTimeoutRef.current = setTimeout(() => {
                        setOpenChat(true);
                    }, 30000);
                }
            }
        };
        
        checkAndOpenChat();

        return () => {
            if (autoOpenTimeoutRef.current) {
                clearTimeout(autoOpenTimeoutRef.current);
            }
        };
    }, [openChat]);

    useEffect(() => {
        if (openChat) {
            setEnableScroll(false);
            loadChatHistory();
        }
    }, [openChat]);

    const handleScroll = async () => {
        if (!chatContentRef.current || isLoadingMore || !hasMoreMessages || !enableScroll) return;

        const { scrollTop, scrollHeight, clientHeight } = chatContentRef.current;
        
        // Load more when user scrolls to top 50px of the chat
        if (scrollTop < 50) {
            lastScrollHeightRef.current = scrollHeight;
            lastScrollTopRef.current = scrollTop;
            await loadChatHistory(currentPage + 1);
        }
    };

    useEffect(() => {
        const chatContent = chatContentRef.current;
        if (openChat && chatContent && enableScroll) {
            chatContent.addEventListener('scroll', handleScroll);
            return () => chatContent.removeEventListener('scroll', handleScroll);
        }
    }, [openChat, isLoadingMore, currentPage, hasMoreMessages, enableScroll]);

    // Maintain scroll position after loading more messages
    useEffect(() => {
        const maintainScrollPosition = () => {
            if (chatContentRef.current && lastScrollHeightRef.current && currentPage > 1) {
                const newScrollHeight = chatContentRef.current.scrollHeight;
                const scrollDiff = newScrollHeight - lastScrollHeightRef.current;
                chatContentRef.current.scrollTop = scrollDiff;
            }
        };

        if (isLoadingMore) {
            // Use requestAnimationFrame to ensure DOM has updated
            requestAnimationFrame(maintainScrollPosition);
        }
    }, [messages, currentPage]);

    const loadChatHistory = async (page = 1) => {
        try {
            if (page === 1) {
                setIsLoading(true);
                setEnableScroll(false);
            } else {
                setIsLoadingMore(true);
            }
            setError(null);
            
            const token = localStorage.getItem('token');
            const response = await roiChatService.getMessages(token, page);
            
            if (response.status) {
                const transformedMessages = response.data.map(msg => ({
                    id: msg.id,
                    text: msg.message,
                    sender: msg.sender_id === 0 ? 'bot' : 'user',
                    timestamp: new Date(msg.created_at).toLocaleTimeString()
                }));

                if (page === 1) {
                    if (transformedMessages.length === 0) {
                        const welcomeMessage = {
                            id: 0,
                            text: `Hello! My name is ROI, your AI guidance counselor! I'm here to help you with your educational and career-related questions. Feel free to ask me about college choices, career paths, study strategies, or any other academic concerns you may have.\n\nBy using this chat service, you abide by our terms and conditions. Please note that while I strive to be helpful and accurate, my responses should be verified with appropriate educational institutions and professionals.`,
                            sender: 'bot',
                            timestamp: new Date().toLocaleTimeString()
                        };
                        setMessages([welcomeMessage]);
                    } else {
                        setMessages(transformedMessages);
                    }
                } else {
                    setMessages(prevMessages => [...transformedMessages, ...prevMessages]);
                }

                // Update pagination state
                setCurrentPage(page);
                setHasMoreMessages(response.pagination.current_page < response.pagination.last_page);
            } else {
                setError(response.message || 'Failed to load chat history');
            }
        } catch (err) {
            setError('Failed to load chat history. Please try again later.');
            console.error('Error loading chat history:', err);
        } finally {
            if (page === 1) {
                setIsLoading(false);
            } else {
                setIsLoadingMore(false);
            }
        }
    };

    const handleToggleChat = () => setOpenChat(!openChat);

    const handleMessageChange = (event) => {
        setMessage(event?.target?.value);
    };

    const handleMessageClick = (event) => {
        // Check if the clicked element has the terms-link class
        if (event.target.classList.contains('terms-link')) {
            setShowTermsModal(true);
        }
    };

    const sendMessage = async (messageText) => {
        const userMessage = {
            id: messages.length + 1,
            text: messageText,
            sender: 'user',
            timestamp: new Date().toLocaleTimeString()
        };

        try {
            setIsLoading(true);
            setError(null);
            
            // Add user message immediately for better UX
            setMessages(prevMessages => [...prevMessages, userMessage]);
            
            // Add typing indicator
            const typingMessage = {
                id: 'typing',
                text: '',
                sender: 'bot',
                timestamp: new Date().toLocaleTimeString(),
                isTyping: true
            };
            setMessages(prevMessages => [...prevMessages, typingMessage]);
            
            // Send message to API
            const token = localStorage.getItem('token');
            const response = await roiChatService.sendMessage(token, messageText);
            if (response.status) {
                // Transform the backend response into the expected format
                const transformedMessages = response.data.map(msg => ({
                    id: msg.id,
                    text: msg.message,
                    sender: msg.sender_id === 0 ? 'bot' : 'user',
                    timestamp: new Date(msg.created_at).toLocaleTimeString()
                }));
                // Remove typing indicator and update messages with the transformed data
                setMessages(transformedMessages);
            } else {
                setError(response.message || 'Failed to send message');
                // Remove the user message and typing indicator if the API call failed
                setMessages(prevMessages => prevMessages.filter(msg => msg.id !== userMessage.id && msg.id !== 'typing'));
            }
        } catch (err) {
            setError('Failed to send message. Please try again.');
            console.error('Error sending message:', err);
            // Remove the user message and typing indicator if the API call failed
            setMessages(prevMessages => prevMessages.filter(msg => msg.id !== userMessage.id && msg.id !== 'typing'));
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyPress = (event) => {
        if (event.key === 'Enter' && !event.shiftKey && message.trim()) {
            event.preventDefault();
            sendMessage(message);
            setMessage('');
        }
    };

    const handleCloseSnackbar = () => {
        setShowErrorSnackbar(false);
        setError(null);
    };

    useEffect(() => {
        if (error) {
            setShowErrorSnackbar(true);
        }
    }, [error]);

    const formatMessage = (text) => {
        if (!text) return '';
        
        // First, escape any existing HTML to prevent XSS
        const escapeHtml = (str) => {
            const div = document.createElement('div');
            div.textContent = str;
            return div.innerHTML;
        };
        
        let formattedText = escapeHtml(text);
        
        // Convert numbered lists (e.g., "1. **Research**" -> proper HTML list)
        formattedText = formattedText.replace(/(\d+\.\s+\*\*[^*]+\*\*[^1]*?)(?=\d+\.|$)/g, (match) => {
            const items = match.split(/(?=\d+\.)/).filter(item => item.trim());
            if (items.length > 0) {
                const listItems = items.map(item => {
                    const cleanItem = item.replace(/^\d+\.\s+/, '').trim();
                    return `<li>${cleanItem}</li>`;
                }).join('');
                return `<ol style="margin: 8px 0; padding-left: 20px;">${listItems}</ol>`;
            }
            return match;
        });
        
        // Convert bold text (**text** -> <strong>text</strong>)
        formattedText = formattedText.replace(/\*\*([^*]+)\*\*/g, '<strong style="font-weight: 600;">$1</strong>');
        
        // Convert line breaks to proper paragraphs
        formattedText = formattedText.split('\n\n').map(paragraph => {
            if (paragraph.trim()) {
                return `<p style="margin: 8px 0; line-height: 1.5;">${paragraph.trim()}</p>`;
            }
            return '';
        }).join('');
        
        // Handle single line breaks within paragraphs
        formattedText = formattedText.replace(/<p[^>]*>(.*?)<\/p>/g, (match, content) => {
            const withLineBreaks = content.replace(/\n/g, '<br>');
            return match.replace(content, withLineBreaks);
        });
        
        return formattedText;
    };

    return (
        <>
            <Box className={styles.chatContainer}>
                {openChat && (
                    <Paper className={styles.chatWindow}>
                        <Box className={styles.chatHeader}>
                            <div className={styles.chatTitle}>
                                <img src={owlLogo} alt="ROI Owl" className={styles.chatTitleIcon} />
                                <Typography variant="h6" sx={{ fontWeight: 500, letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: 1 }}>
                                    Chat With ROI
                                    <Tooltip title="Learn about ROI">
                                        <IconButton 
                                            size="small" 
                                            onClick={() => setShowInfoModal(true)}
                                            sx={{ color: 'white', padding: '2px', marginLeft: '-5px' }}
                                        >
                                            <InfoOutlinedIcon sx={{ fontSize: 14 }} />
                                        </IconButton>
                                    </Tooltip>
                                </Typography>
                            </div>
                            <IconButton onClick={handleToggleChat} size="small">
                                <CloseIcon className={styles.closeIcon} />
                            </IconButton>
                        </Box>
                        <Box 
                            className={styles.chatContent} 
                            onClick={handleMessageClick}
                            ref={chatContentRef}
                        >
                            {isLoadingMore && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', p: 1 }}>
                                    <CircularProgress size={20} />
                                </Box>
                            )}
                            {messages.map((msg) => (
                                <div key={msg.id} className={msg.sender === 'user' ? styles.userMessage : styles.botMessage}>
                                    {msg.isTyping ? (
                                        <TypingIndicator />
                                    ) : (
                                        <>
                                            <div 
                                                dangerouslySetInnerHTML={{ 
                                                    __html: msg.sender === 'bot' ? formatMessage(msg.text) : (msg.text || '') 
                                                }}
                                            />
                                            <div className={styles.messageTime}>{msg.timestamp}</div>
                                        </>
                                    )}
                                </div>
                            ))}
                            <div ref={messagesEndRef} />
                        </Box>
                        <Box className={styles.chatInputContainer}>
                            <textarea
                                className={styles.chatInput}
                                value={message}
                                onChange={handleMessageChange}
                                onKeyPress={handleKeyPress}
                                placeholder="Type your message..."
                                rows={2}
                                disabled={isLoading}
                                maxLength={250}
                            />
                            <Typography variant="caption" sx={{ mt: 0.5, textAlign: 'right', display: 'block', width: '100%', fontSize: '0.7rem', color: 'text.secondary' }}>
                                <span style={{ color: message.length > 225 ? '#f44336' : 'inherit' }}>
                                    {message.length}/250
                                </span>
                            </Typography>
                            <Typography variant="caption" sx={{ mt: 0.5, textAlign: 'center', display: 'block', width: '100%', fontSize: '0.7rem', color: 'text.secondary' }}>
                                <Link 
                                    component="button"
                                    variant="caption" 
                                    onClick={() => setShowTermsModal(true)}
                                    sx={{ textDecoration: 'underline', fontSize: '0.7rem', color: 'text.secondary' }}
                                >
                                    By using this chat, you agree to our terms of service
                                </Link>
                            </Typography>
                        </Box>
                    </Paper>
                )}
                <Box className={styles.fabContainer}>
                    <Tooltip title={!openChat ? "Chat with ROI our AI guidance counselor." : ""} arrow placement="left">
                        <Fab color="primary" onClick={handleToggleChat}>
                            <AutoAwesomeIcon className={styles.chatIcon} />
                        </Fab>
                    </Tooltip>
                </Box>
            </Box>
            <Snackbar 
                open={showErrorSnackbar} 
                autoHideDuration={6000} 
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                sx={{ position: 'fixed', bottom: '20px', left: '20px' }}
            >
                <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
                    {error}
                </Alert>
            </Snackbar>
            <Modal
                open={showTermsModal}
                onClose={() => setShowTermsModal(false)}
            >
                <Box sx={termsStyle}>
                    <Typography variant="h4" component="h2" gutterBottom>
                        AI Chat Terms and Conditions
                    </Typography>
                    <Typography variant="body1" paragraph>
                        Welcome to ROI, your AI guidance counselor. Please review the following terms of service:
                    </Typography>
                    <Typography variant="body1" paragraph>
                        1. Purpose: ROI is an AI-powered chat service designed to provide educational guidance and career-related information. While it strives to be helpful and accurate, it should not be considered a replacement for professional guidance counselors or academic advisors.
                    </Typography>
                    <Typography variant="body1" paragraph>
                        2. Privacy: Your chat interactions are stored securely to provide personalized assistance and improve our service. We handle your data in accordance with our privacy policy.
                    </Typography>
                    <Typography variant="body1" paragraph>
                        3. Limitations: ROI provides general guidance based on available information. All recommendations should be verified with appropriate educational institutions and professionals.
                    </Typography>
                    <Typography variant="body1" paragraph>
                        4. Usage: By using this service, you agree to interact respectfully and understand that misuse may result in service restriction.
                    </Typography>
                    <Typography variant="body1" paragraph>
                        5. Daily Limits: You are limited to 100 chats per day. If you need to increase this limit for educational or business purposes, please contact our support team at support@educationadvisor.org.
                    </Typography>
                    <Typography variant="body1" paragraph>
                        For more information, please refer to our&nbsp;
                        <Link href="https://educationadvisor.org/terms-of-use/" target="_blank" rel="noopener">
                            Terms of Service
                        </Link> and&nbsp;
                        <Link href="https://educationadvisor.org/privacy-policy/" target="_blank" rel="noopener">
                            Privacy Policy
                        </Link>
                    </Typography>
                    <Box sx={{ mt: 3, textAlign: 'right' }}>
                        <Button onClick={() => setShowTermsModal(false)} variant="contained" color="primary" disableElevation>
                            Got it
                        </Button>
                    </Box>
                </Box>
            </Modal>
            <Modal
                open={showInfoModal}
                onClose={() => setShowInfoModal(false)}
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    maxWidth: 500,
                    width: '90%',
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: 2,
                    outline: 'none',
                }}>
                    <Typography variant="h5" component="h2" gutterBottom sx={{ color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <img src={owlLogo} alt="ROI Owl" style={{ width: 32, height: 32 }} />
                        Meet ROI
                    </Typography>
                    <Typography variant="body1" paragraph>
                        ROI (Return on Investment) is your AI-powered guidance counselor, designed to help you navigate your educational journey with personalized advice and support.
                    </Typography>
                    <Typography variant="body1" paragraph>
                        As an advanced AI assistant, ROI can help you with:
                    </Typography>
                    <ul style={{ marginBottom: '16px' }}>
                        <li>Educational planning and career guidance</li>
                        <li>College and course selection advice</li>
                        <li>Study strategies and academic support</li>
                        <li>Career path exploration and planning</li>
                        <li>Answering questions about admissions and applications</li>
                    </ul>
                    <Typography variant="body1">
                        While ROI provides valuable guidance, remember that all recommendations should be verified with appropriate educational institutions and professionals.
                    </Typography>
                    <Typography variant="body1" paragraph sx={{mt: 2}}>
                        <strong>Daily Limit Notice:</strong> You are limited to 100 chats per day. If you need to increase this limit for educational or business purposes, please contact our support team at support@educationadvisor.org.
                    </Typography>
                    <Box sx={{ mt: 3, textAlign: 'right' }}>
                        <Button onClick={() => setShowInfoModal(false)} variant="contained" color="primary" disableElevation>
                            Got it
                        </Button>
                    </Box>
                </Box>
            </Modal>
        </>
    );
}